import ButtonsBlock from '@/components/common/buttons-block/buttons-block';
import DialogTemplate from '@/components/common/dialog-template/dialog-template';
import FormInputsSeparator from '@/components/common/form-inputs-separator/form-inputs-separator';
import useGetAvatar from '@/hooks/useGetAvatar';
import useGetLogoImage from '@/hooks/useGetLogoImage';
import { composeUri } from '@/utils/compose';
import {
    AVATAR_RESOURCE_SUPPORTED,
    AVATARS,
    CLIENTS,
    MEDIA,
    MEDIA_IMAGE_TYPE,
    MEDIAS,
    TICKETING_LOGO_MEDIA_TAG_NAME,
} from '@/utils/CONST';
import { getFileBase64 } from '@/utils/files';
import extractIdFromURI from '@/utils/id';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import LoadingButton from '@mui/lab/LoadingButton';
import { Button } from '@mui/material';
import Box from '@mui/material/Box';
import { get } from 'lodash';
import {
    Form,
    ImageField,
    ImageInput,
    required,
    TextInput,
    useCreate,
    useDelete,
    useNotify,
    useTranslate,
    useUpdate,
} from 'react-admin';
interface EditClientDialogProps {
    open: boolean;
    onClose: () => void;
    clientId: string;
}

const EditClientDialog = ({ open, onClose, clientId }: EditClientDialogProps) => {
    const translate = useTranslate();
    const { avatar, isLoading: isLoadingAvatar } = useGetAvatar({
        avatarUriId: composeUri(AVATARS, clientId ?? ''),
        queryKey: CLIENTS,
    });
    const {
        mediaData,
        isLoading: isLoadingMediaData,
        refetch: refetchMediaData,
    } = useGetLogoImage({
        avatarId: extractIdFromURI(avatar?.id) ?? undefined,
    });
    const notify = useNotify();
    const [update, { isLoading: isLoadingUpdate }] = useUpdate();
    const [, { isLoading: isLoadingDelete, mutateAsync: deleteOne }] = useDelete();
    const [create, { isLoading: isLoadingCreate }] = useCreate();

    const handleSubmit = async ({ name, media }: any) => {
        if (name !== avatar.name) {
            await update(
                CLIENTS,
                {
                    id: avatar.id,
                    data: {
                        name,
                    },
                    meta: {
                        realResource: AVATARS,
                    },
                },
                {
                    returnPromise: true,

                },
            );
        }

        if (!!mediaData && get(media, 'src') !== get(mediaData, 'downloadUrl')) {
            await deleteOne({
                resource: MEDIA,
                id: mediaData?.id,
            });
        }

        if (!!media && get(media, 'src') !== get(mediaData, 'downloadUrl')) {
            const fileData: any = await getFileBase64(media.rawFile);

            const finalMedia = {
                fileName: media.title,
                base64data: fileData,
                mediaType: MEDIA_IMAGE_TYPE,
                resourceSupported: AVATAR_RESOURCE_SUPPORTED,
                resourceId: extractIdFromURI(avatar?.id),
                tags: [TICKETING_LOGO_MEDIA_TAG_NAME],
                customFields: {
                    tags: [TICKETING_LOGO_MEDIA_TAG_NAME],
                },
            };

            await create(
                MEDIAS,
                {
                    data: finalMedia as any,
                },
                {
                    returnPromise: true,
                },
            );
        }

        notify('ra.notification.updated', {
            type: 'success',
            messageArgs: {
                smart_count: 1,
            },
        });
        refetchMediaData();
        onClose();
    };

    const isLoading = isLoadingAvatar || isLoadingMediaData || isLoadingUpdate || isLoadingDelete || isLoadingCreate;

    return (
        <DialogTemplate
            icon={<BorderColorIcon />}
            title={translate('client.edit_client_label', {
                clientName: avatar?.name,
            })}
            open={open}
            onClose={onClose}
            closeButtonDisabled={isLoading}
        >
            <Form
                onSubmit={handleSubmit}
                defaultValues={{
                    name: avatar?.name,
                    ...(mediaData && {
                        media: {
                            src: mediaData.downloadUrl,
                        },
                    }),
                }}
            >
                <Box
                    sx={{
                        display: 'grid',
                        gap: 2,
                    }}
                >
                    <FormInputsSeparator>
                        <TextInput
                            source='name'
                            helperText={false}
                            label={'client.form.client_name'}
                            validate={required()}
                        />
                    </FormInputsSeparator>

                    <ImageInput
                        source={'media'}
                        label={'buttons.add_logo'}
                        maxSize={30000000}
                        helperText={false}
                        sx={{
                            '& .RaLabeled-label': {
                                fontSize: 16,
                                color: 'black',
                            },
                            '& .RaFileInput-dropZone': {
                                display: 'grid',
                                alignItems: 'center',
                                border: (theme: any) => `1px solid ${theme.palette.action.disabled}`,
                            },
                            '& .previews': {
                                display: 'flex',
                                justifyContent: 'center',
                            },
                        }}
                    >
                        <ImageField source='src' title='title' />
                    </ImageInput>

                    <ButtonsBlock>
                        <Button onClick={onClose} disabled={isLoading}>
                            {translate('buttons.cancel')}
                        </Button>

                        <LoadingButton type='submit' variant='contained' loading={isLoading}>
                            {translate('buttons.validate')}
                        </LoadingButton>
                    </ButtonsBlock>
                </Box>
            </Form>
        </DialogTemplate>
    );
};

export default EditClientDialog;
